import Swiper from 'swiper';
import { Navigation, Pagination, Mousewheel } from 'swiper/modules';
import { module as Module } from 'modujs';
import { createFocusTrap } from 'focus-trap';
import { $hasGalleryOpen } from '../stores/state';
import { delegateEvent } from '../utils/events';
import { $screenDebounce } from '../stores/screen';
import { $prefersReducedMotion } from '../stores/prefers-reduced-motion';

export default class Gallery extends Module {
    static CLASS = {
        ACTIVE: 'is-active',
        GLOBAL_ACTIVE: 'has-gallery-open'
    };

    static SELECTOR = '[data-gallery-open]';

    constructor(m) {
        super(m);

        // Binding
        this.onResize = this.onResize.bind(this);
        this.onGalleryOpenChange = this.onGalleryOpenChange.bind(this);
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onActivate = this.onActivate.bind(this);
        this.onPostActivate = this.onPostActivate.bind(this);
        this.onDeactivate = this.onDeactivate.bind(this);
        this.onPostDeactivate = this.onPostDeactivate.bind(this);
        this.onPrefersReducedMotionChange = this.onPrefersReducedMotionChange.bind(this);

        // UI
        this.$close = this.$('close')[0];
        this.$top = this.$('top')[0];
        this.$bottom = this.$('bottom')[0];
        this.$prev = this.$('prev')[0];
        this.$next = this.$('next')[0];
        this.$swiper = this.$('swiper')[0];
        this.$pagination = this.$('pagination')[0];
        this.$slides = Array.from(this.$('slide'));

        // Data
        this.galleryId = this.el.dataset.galleryId;

        // Focus trap
        this.focusTrap = createFocusTrap(this.el, {
            onActivate: this.onActivate,
            onPostActivate: this.onPostActivate,
            onDeactivate: this.onDeactivate,
            onPostDeactivate: this.onPostDeactivate,
            clickOutsideDeactivates: true
        });
    }

    // //////////
    // Lifecycle
    // //////////

    init() {
        this.bindEvents();
        this.unbindPrefersReducedMotion = $prefersReducedMotion.subscribe(
            this.onPrefersReducedMotionChange
        );
    }

    destroy() {
        this.unbindEvents();
        this.destroySwiper();
        this.unbindPrefersReducedMotion?.();
        super.destroy();
    }

    // //////////
    // Events
    // //////////
    bindEvents() {
        this.unbindOpenEvent = delegateEvent(document.body, 'click', Gallery.SELECTOR, this.onOpen);
        this.unbindGalleryOpenListener = $hasGalleryOpen.listen(this.onGalleryOpenChange);
        this.$close?.addEventListener('click', this.onClose);
        this.unbindScreenListener = $screenDebounce.subscribe(this.onResize);
    }

    unbindEvents() {
        this.unbindOpenEvent.abort?.();
        this.unbindGalleryOpenListener?.();
        this.$close?.addEventListener('click', this.onClose);
        this.unbindScreenListener?.();
    }

    // //////////
    // Callbacks
    // //////////
    onActivate() {
        this.el.setAttribute('aria-hidden', false);
    }

    onPostActivate() {
        this.$triggerCta?.setAttribute('aria-expanded', true);
    }

    onDeactivate() {
        this.el.classList.remove(Gallery.CLASS.ACTIVE);
        document.documentElement.classList.remove(Gallery.CLASS.GLOBAL_ACTIVE);

        $hasGalleryOpen.set({
            state: false,
            id: null
        });

        this.el.setAttribute('aria-hidden', true);
    }

    onPostDeactivate() {
        this.$triggerCta?.setAttribute('aria-expanded', false);
    }

    onGalleryOpenChange(hasGalleryOpen) {
        if (hasGalleryOpen.state && hasGalleryOpen.id === this.galleryId) {
            this.el.classList.add(Gallery.CLASS.ACTIVE);
            document.documentElement.classList.add(Gallery.CLASS.GLOBAL_ACTIVE);

            this.swiper.setProgress(0, 0);

            requestAnimationFrame(() => {
                this.focusTrap?.activate?.();
            });
        }
    }

    onOpen(e) {
        this.$triggerCta = e?.target;
        const galleryId = this.$triggerCta?.dataset?.galleryId;
        this.open(galleryId);
    }

    onClose() {
        this.close();
    }

    onResize({ width, height }) {
        this.setMetrics();
    }

    onPrefersReducedMotionChange() {
        this.destroySwiper();
        this.initSwiper();

        requestAnimationFrame(() => {
            this.setMetrics();
        });
    }

    // //////////
    // Methods
    // //////////

    // Modal
    open(galleryId) {
        if ($hasGalleryOpen.value.state) return;

        $hasGalleryOpen.set({
            state: true,
            id: galleryId
        });
    }

    close() {
        if (!$hasGalleryOpen.value.state) return;
        this.$triggerCta = null;
        this.focusTrap?.deactivate?.();
    }

    // Swiper
    initSwiper() {
        const args = {
            modules: [Navigation, Pagination, Mousewheel],
            speed: $prefersReducedMotion.value ? 100 : 400,
            effect: $prefersReducedMotion.value ? 'fade' : 'slide',
            loop: true,
            slidesPerView: 'auto',
            spaceBetween: 20,
            mousewheel: {
                enenabled: true
            },
            navigation: {
                prevEl: this.$prev,
                nextEl: this.$next
            },
            pagination: {
                el: this.$pagination,
                type: 'fraction',
                formatFractionCurrent: (number) => {
                    return String(number).padStart(2, '0');
                },
                formatFractionTotal: (number) => {
                    return String(number).padStart(2, '0');
                },
                renderFraction: (currentClass, totalClass) => {
                    return `<span class="${currentClass}"></span>&nbsp;<span><span>/</span>&nbsp;<span class="${totalClass}"></span></span>`;
                }
            }
        };

        this.swiper = new Swiper(this.$swiper, args);
        this.swiper.autoplay?.pause();
    }

    destroySwiper() {
        this.swiper?.destroy(true, true);
    }

    // Others
    setMetrics() {
        this.el.style.setProperty('--gallery-top-height', this.$top.offsetHeight + 'px');
        this.el.style.setProperty('--gallery-bottom-height', this.$bottom.offsetHeight + 'px');

        requestAnimationFrame(() => {
            this.swiper?.update();
        });
    }
}
